/**
 * Auth via Azure SWA built-in authentication (/.auth/login/aad).
 *
 * SWA manages the session cookie — no MSAL token handling needed.
 * The session cookie is sent automatically with every /api/* fetch.
 * User identity is available in functions via x-ms-client-principal header.
 *
 * Login:  window.location.href = '/.auth/login/aad'
 * NOTE:   The "azureActiveDirectory" key in staticwebapp.config.json overrides
 *         WHICH Entra ID app registration SWA uses — the login endpoint is
 *         always /.auth/login/aad regardless of the JSON key name.
 * Logout: window.location.href = '/.auth/logout'
 * Me:     GET /.auth/me → { clientPrincipal: { userDetails, userRoles } }
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { loadAuthSession } from './session'
import { SitzungAbgelaufenDialog } from '../shared/ui/SitzungAbgelaufen'
import { giltEntra } from '../shared/sitzungBereich'

interface SwaClaim { typ: string; val: string }
interface SwaUser {
  userId: string
  userDetails: string         // email / UPN
  userRoles: string[]
  identityProvider: string
  claims?: SwaClaim[]
  /** Derived display name ("Ömer Beispiel"). Falls back to the UPN prefix. */
  displayName?: string
}

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: SwaUser | null
  /** @param redirectTo Path (+ search/hash) to return to after login, e.g.
   *  location.pathname + location.search + location.hash. Omit for the
   *  default post-login landing. */
  login: (redirectTo?: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<SwaUser | null>(null)
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    void (async () => {
      try {
        const { user } = await loadAuthSession()
        setUser(user as SwaUser | null)
      } finally {
        setLoading(false)
      }
    })()

    // Load runtime feature flags once at bootstrap. Kill-switch for Phase-1
    // role-admin. Fire-and-forget — failure leaves flags empty (= OFF).
    void import('../store/featureFlagsStore').then(m => m.useFeatureFlagsStore.getState().load())
  }, [])

  const login  = (redirectTo?: string) => {
    const suffix = redirectTo ? `?post_login_redirect_uri=${encodeURIComponent(redirectTo)}` : ''
    window.location.href = `/.auth/login/aad${suffix}`
  }
  const logout = () => { window.location.href = '/.auth/logout?post_logout_redirect_uri=/' }

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!user, isLoading, user, login, logout }}>
      {children}
      {/* §6.59 — abgelaufene Entra-Sitzung: der naechste API-Aufruf liefert
         401; statt roher Fehlermeldungen ein Dialog mit dem Weg zurueck
         auf genau diese Seite. */}
      <SitzungAbgelaufenDialog
        aktiv={!!user}
        gilt={giltEntra}
        titel="Deine Anmeldung ist abgelaufen"
        text="Du warst eine Weile nicht aktiv. Bitte melde dich neu an — danach geht es genau hier weiter."
        knopf="Neu anmelden"
        onNeuAnmelden={() => { window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.pathname + window.location.search)}` }}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
